import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  Input,
  Select,
  Button,
  Space,
  Row,
  Col,
  message,
  Spin,
  FloatButton,
  Popconfirm,
  Typography,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import mediaService, {
  MediaFile,
  MediaListParams,
  MediaListResponse,
  MediaStats
} from '../../services/media';
import MediaGridView from './MediaGridView';
import MediaUploader from './MediaUploader';

const { Option } = Select;
const { Text } = Typography;

interface MediaLibraryModalProps {
  visible: boolean;
  onCancel: () => void;
  onSelect?: (mediaFiles: MediaFile | MediaFile[]) => void;
  selectionMode?: 'single' | 'multiple' | 'none';
  fileTypes?: string[]; // 限制可选择的文件类型
  maxSelection?: number;
  title?: string;
}

const MediaLibraryModal: React.FC<MediaLibraryModalProps> = ({
  visible,
  onCancel,
  onSelect,
  selectionMode = 'none',
  fileTypes,
  maxSelection,
  title = '媒体库',
}) => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [stats, setStats] = useState<MediaStats['data'] | null>(null);
  const [showUploader, setShowUploader] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // 滚动容器引用
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 搜索和筛选
  const [searchParams, setSearchParams] = useState<MediaListParams>({
    page: 1,
    per_page: 50, // 默认50张图片
    sort_by: 'upload_date',
    sort_order: 'desc',
  });

  // 加载媒体列表
  const loadMediaList = useCallback(async (params: MediaListParams = searchParams, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response: MediaListResponse = await mediaService.getMediaList(params);
      if (response.success) {
        let filteredData = response.data.data;

        // 根据fileTypes过滤
        if (fileTypes && fileTypes.length > 0) {
          filteredData = filteredData.filter(media =>
            fileTypes.includes(media.file_type)
          );
        }

        if (append) {
          // 追加数据
          setMediaFiles(prev => [...prev, ...filteredData]);
        } else {
          // 替换数据
          setMediaFiles(filteredData);
        }

        // 检查是否还有更多数据
        setHasMore(response.data.current_page < response.data.last_page);
      }
    } catch (error: any) {
      console.error('Failed to load media list:', error);
      message.error('加载媒体列表失败');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchParams, fileTypes]);

  // 加载更多数据
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;

    const nextParams = {
      ...searchParams,
      page: Math.floor(mediaFiles.length / searchParams.per_page!) + 1,
    };

    loadMediaList(nextParams, true);
  }, [searchParams, mediaFiles.length, loadingMore, hasMore, loadMediaList]);

  // 滚动处理
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    // 距离底部100px时开始加载
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMore();
    }
  }, [loadMore]);

  // 加载统计信息
  const loadStats = async () => {
    try {
      const response = await mediaService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // 初始化加载或默认筛选
  useEffect(() => {
    if (visible) {
      setSelectedIds([]); // 重置选择
      const nextParams = { ...searchParams, page: 1 };
      if (fileTypes && fileTypes.length === 1) {
        nextParams.file_type = fileTypes[0];
      }
      setSearchParams(nextParams);
      loadMediaList(nextParams);
      loadStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // 搜索参数变化时重新加载
  useEffect(() => {
    if (visible) {
      loadMediaList(searchParams);
    }
  }, [searchParams.search, searchParams.file_type, searchParams.date_from, searchParams.date_to, searchParams.sort_by, searchParams.sort_order, visible, loadMediaList]);

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchParams(prev => ({
      ...prev,
      search: value || undefined,
      page: 1,
    }));
  };

  // 处理文件类型筛选
  const handleFileTypeChange = (value: string) => {
    setSearchParams(prev => ({
      ...prev,
      file_type: value || undefined,
      page: 1,
    }));
  };


  // 处理选择变化
  const handleSelectionChange = (ids: number[]) => {
    if (maxSelection && ids.length > maxSelection) {
      message.warning(`最多只能选择 ${maxSelection} 个文件`);
      return;
    }
    setSelectedIds(ids);
  };

  // 处理上传成功
  const handleUploadSuccess = (mediaFile: MediaFile) => {
    setMediaFiles(prev => [mediaFile, ...prev]);
    loadStats(); // 更新统计信息
    setShowUploader(false); // 关闭上传器
  };

  // 处理媒体更新
  const handleMediaUpdate = (updatedMedia: MediaFile) => {
    setMediaFiles(prev =>
      prev.map(media =>
        media.id === updatedMedia.id ? updatedMedia : media
      )
    );
  };

  // 处理媒体删除
  const handleMediaDelete = async (id: number) => {
    try {
      await mediaService.deleteMedia(id);
      setMediaFiles(prev => prev.filter(media => media.id !== id));
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
      message.success('删除成功');
      loadStats(); // 更新统计信息
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) {
      message.warning('请先选择要删除的文件');
      return;
    }

    try {
      await mediaService.batchDelete(selectedIds);
      setMediaFiles(prev =>
        prev.filter(media => !selectedIds.includes(media.id))
      );
      setSelectedIds([]);
      message.success(`成功删除 ${selectedIds.length} 个文件`);
      loadStats(); // 更新统计信息
    } catch (error: any) {
      message.error(error.message || '批量删除失败');
    }
  };

  // 处理选择确认
  const handleSelectConfirm = () => {
    if (!onSelect) return;

    const selectedMedia = mediaFiles.filter(media =>
      selectedIds.includes(media.id)
    );

    if (selectedMedia.length === 0) {
      message.warning('请先选择文件');
      return;
    }

    if (selectionMode === 'single') {
      onSelect(selectedMedia[0]);
    } else {
      onSelect(selectedMedia);
    }

    onCancel();
  };

  // 重置筛选
  const handleReset = () => {
    setSearchParams({
      page: 1,
      per_page: 50,
      sort_by: 'upload_date',
      sort_order: 'desc',
    });
  };

  return (
    <>
      <Modal
        title={
          <Space>
            {title}
            {selectionMode !== 'none' && selectedIds.length > 0 && (
              <Text type="secondary">
                已选择 {selectedIds.length} 个文件
              </Text>
            )}
            {stats && (
              <Text type="secondary">
                · 总计 {stats.total_files} 个文件 · {stats.formatted_total_size}
              </Text>
            )}
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        width="90%"
        style={{ maxWidth: 1200 }}
        footer={null}
      >
        {/* 筛选工具栏 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {/* 左侧筛选表单 */}
          <Col span={12}>
            <Space>
              <Input.Search
                placeholder="搜索文件名、标题..."
                allowClear
                onSearch={handleSearch}
                style={{ width: 250 }}
              />
              <Select
                placeholder="文件类型"
                allowClear
                showSearch
                style={{ width: 120 }}
                onChange={handleFileTypeChange}
                value={searchParams.file_type}
              >
                <Option value="image">图片</Option>
                <Option value="document">文档</Option>
                <Option value="video">视频</Option>
                <Option value="audio">音频</Option>
                <Option value="other">其他</Option>
              </Select>
            </Space>
          </Col>

          {/* 右侧按钮组 */}
          <Col span={12} style={{ textAlign: 'right' }}>
            <Space>
              {selectionMode !== 'none' && (
                <Button
                  type="primary"
                  onClick={handleSelectConfirm}
                  disabled={selectedIds.length === 0}
                >
                  确认选择
                </Button>
              )}
              <Button
                icon={<UploadOutlined />}
                type="primary"
                onClick={() => setShowUploader(true)}
              >
                上传文件
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadMediaList()}
              >
                刷新
              </Button>
              <Button onClick={handleReset}>
                重置
              </Button>
              {selectionMode !== 'none' && selectedIds.length > 0 && (
                <Popconfirm
                  title="确定要删除选中的文件吗？"
                  onConfirm={handleBatchDelete}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    删除选中
                  </Button>
                </Popconfirm>
              )}
              <Button onClick={onCancel}>取消</Button>
            </Space>
          </Col>
        </Row>

        {/* 媒体网格视图 - 支持无限滚动 */}
        <div
          ref={scrollContainerRef}
          style={{
            height: 600,
            overflowY: 'auto',
            padding: '0 8px',
          }}
          onScroll={handleScroll}
        >
          <MediaGridView
            mediaFiles={mediaFiles}
            loading={loading}
            selectable={selectionMode !== 'none'}
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            onMediaUpdate={handleMediaUpdate}
            onMediaDelete={handleMediaDelete}
          />

          {/* 加载更多指示器 */}
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Spin /> <Text type="secondary">加载更多...</Text>
            </div>
          )}

          {/* 没有更多数据提示 */}
          {!hasMore && mediaFiles.length > 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
              <Text type="secondary">已显示全部 {mediaFiles.length} 个文件</Text>
            </div>
          )}
        </div>
      </Modal>

      {/* 上传按钮 */}
      <FloatButton.Group
        trigger="click"
        type="primary"
        icon={<PlusOutlined />}
        tooltip="添加媒体"
        style={{ display: visible ? 'block' : 'none' }}
      >
        <FloatButton
          icon={<UploadOutlined />}
          tooltip="上传文件"
          onClick={() => setShowUploader(true)}
        />
      </FloatButton.Group>

      {/* 上传器模态框 */}
      <Modal
        title="上传文件"
        open={showUploader}
        onCancel={() => setShowUploader(false)}
        footer={null}
        width={600}
      >
        <MediaUploader
          onUploadSuccess={handleUploadSuccess}
          onUploadError={(error) => {
            console.error('Upload error:', error);
            message.error('上传失败');
          }}
          multiple
          maxSize={50}
          accept={fileTypes ? fileTypes.map(type => {
            switch(type) {
              case 'image': return 'image/*';
              case 'document': return '.pdf,.doc,.docx,.txt';
              case 'video': return 'video/*';
              case 'audio': return 'audio/*';
              default: return '*';
            }
          }).join(',') : undefined}
        />
      </Modal>
    </>
  );
};

export default MediaLibraryModal;
